// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { SuperadminService } from "@/services/superadmin";
import { OrgFilters } from "@/components/superadmin/organizations/OrgFilters";
import { OrgTable } from "@/components/superadmin/organizations/OrgTable";
import { OrgDetail } from "@/components/superadmin/organizations/OrgDetail";
import { SuspendOrgDialog } from "@/components/superadmin/organizations/SuspendOrgDialog";
import { DeleteOrgDialog } from "@/components/superadmin/organizations/DeleteOrgDialog";
import { features } from "@/lib/config";
import type { SuperadminOrganization } from "@/types/superadmin";

export interface SearchParams {
  page: number;
  search: string;
  plan: string;
  status: string;
  sortBy: string;
  sortOrder: string;
}

export const Route = createFileRoute("/superadmin/organizations")({
  component: OrganizationsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || "",
    plan: (search.plan as string) || "all",
    status: (search.status as string) || "all",
    sortBy: (search.sortBy as string) || "created",
    sortOrder: (search.sortOrder as string) || "desc",
  }),
});

function OrganizationsPage() {
  const { t } = useTranslation("superadmin");
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const params = Route.useSearch();

  // Debounced search
  const [searchInput, setSearchInput] = useState(params.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchInput !== params.search) {
        updateParams({ search: searchInput, page: 1 });
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Sync search input when URL changes externally
  useEffect(() => {
    setSearchInput(params.search);
  }, [params.search]);

  const updateParams = useCallback(
    (updates: Partial<SearchParams>) => {
      navigate({
        search: { ...params, ...updates },
        replace: true,
      });
    },
    [navigate, params],
  );

  // Query
  const { data, isLoading } = useQuery({
    queryKey: ["superadmin", "organizations", params],
    queryFn: () =>
      SuperadminService.listOrganizations({
        ...params,
        plan: features.payments ? params.plan : "all",
      }),
  });

  // Dialog state
  const [selectedOrg, setSelectedOrg] =
    useState<SuperadminOrganization | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const invalidateOrgs = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["superadmin", "organizations"] });
  }, [queryClient]);

  // Mutations
  const suspendMutation = useMutation({
    mutationFn: SuperadminService.suspendOrganization,
    onSuccess: () => {
      invalidateOrgs();
      toast.success(t("organizations.suspend.success"));
      setSuspendDialogOpen(false);
    },
    onError: () => toast.error(t("organizations.suspend.error")),
  });

  const activateMutation = useMutation({
    mutationFn: SuperadminService.activateOrganization,
    onSuccess: () => {
      invalidateOrgs();
      toast.success(t("organizations.activate.success"));
    },
    onError: () => toast.error(t("organizations.activate.error")),
  });

  const deleteMutation = useMutation({
    mutationFn: SuperadminService.deleteOrganization,
    onSuccess: () => {
      invalidateOrgs();
      toast.success(t("organizations.delete.success"));
      setDeleteDialogOpen(false);
    },
    onError: () => toast.error(t("organizations.delete.error")),
  });

  const handleAction = useCallback(
    (action: string, org: SuperadminOrganization) => {
      setSelectedOrg(org);
      switch (action) {
        case "view":
          setDetailOpen(true);
          break;
        case "suspend":
          setSuspendDialogOpen(true);
          break;
        case "activate":
          activateMutation.mutate(org.id);
          break;
        case "delete":
          setDeleteDialogOpen(true);
          break;
      }
    },
    [activateMutation],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">
          {t("organizations.title")}
        </h1>
        <p className="text-muted-foreground">
          {t(
            features.payments
              ? "organizations.description"
              : "organizations.descriptionNoPayments",
          )}
        </p>
      </div>

      <OrgFilters
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        plan={features.payments ? params.plan : "all"}
        status={params.status}
        onFilterChange={(key, value) =>
          updateParams({ [key]: value, page: 1 })
        }
        onClear={() =>
          updateParams({
            search: "",
            plan: "all",
            status: "all",
            page: 1,
          })
        }
      />

      <OrgTable
        organizations={data?.organizations ?? []}
        pagination={data?.pagination}
        isLoading={isLoading}
        sortBy={params.sortBy}
        sortOrder={params.sortOrder}
        onSort={(sortBy, sortOrder) => updateParams({ sortBy, sortOrder })}
        onPageChange={(page) => updateParams({ page })}
        onAction={handleAction}
      />

      {selectedOrg && (
        <>
          <OrgDetail
            organizationId={selectedOrg.id}
            open={detailOpen}
            onOpenChange={setDetailOpen}
          />

          <SuspendOrgDialog
            org={selectedOrg}
            open={suspendDialogOpen}
            onOpenChange={setSuspendDialogOpen}
            onConfirm={() => suspendMutation.mutate(selectedOrg.id)}
            isLoading={suspendMutation.isPending}
          />

          <DeleteOrgDialog
            org={selectedOrg}
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            onConfirm={(password) =>
              deleteMutation.mutate({
                organizationId: selectedOrg.id,
                password,
              })
            }
            isLoading={deleteMutation.isPending}
          />
        </>
      )}
    </div>
  );
}
